import { useQuery } from '@tanstack/react-query';
import { getDocument, getDocuments, getDraw, getDrawDropdown, getDraws, getInvoice, getInvoices } from '@/api/Sherpa/Draw';
import { SherpaProjectReactQueryKey } from '../../../constants/ReactQueryKeys';
import type { QueryParamsInterface } from '../../../types/Shared';

interface DrawsHookProps {
  projectId: number;
  params?: QueryParamsInterface;
  onSuccess?: (data: any) => void;
  onError?: (data: any) => void;
}

export const useDraws = ({ projectId, params, onSuccess, onError }: DrawsHookProps) => {
  //Tanstack: version 5 query implementation
  return useQuery({
    queryKey: [SherpaProjectReactQueryKey.GET_PROJECT_DRAWS, projectId, params],
    queryFn: () => getDraws({ projectId, params }),
    onSuccess,
    onError,
    keepPreviousData: true,
  });
};

interface DrawHookProps {
  projectId: number;
  drawId: number;
  onSuccess?: (data: any) => void;
  onError?: (data: any) => void;
}

export const useDraw = ({ projectId, drawId, onSuccess, onError }: DrawHookProps) => {
  //Tanstack: version 5 query implementation
  return useQuery({
    queryKey: [SherpaProjectReactQueryKey.GET_PROJECT_DRAW, projectId],
    queryFn: () => getDraw({ projectId, drawId }),
    onSuccess,
    onError,
  });
};

export const useDrawDropdown = ({ projectId, onSuccess, onError }: Partial<DrawHookProps>) => {
  //Tanstack: version 5 query implementation
  return useQuery({
    queryKey: [SherpaProjectReactQueryKey.GET_DRAW_LIST],
    queryFn: () => getDrawDropdown({ projectId }),
    onSuccess,
    onError,
    keepPreviousData: true,
  });
};

interface DocumentsHookProps extends DrawsHookProps {
  drawId: number;
}

export const useDocuments = ({ projectId, drawId, params, onSuccess, onError }: DocumentsHookProps) => {
  //Tanstack: version 5 query implementation
  return useQuery({
    queryKey: [SherpaProjectReactQueryKey.GET_PROJECT_DOCUMENTS, projectId, drawId, params],
    queryFn: () => getDocuments({ projectId, drawId, params }),
    onSuccess,
    onError,
    keepPreviousData: true,
  });
};

interface DocumentHookProps extends DrawsHookProps {
  drawId: number;
  documentId: number;
}
export const useDocument = ({ projectId, documentId, drawId, params, onSuccess, onError }: DocumentHookProps) => {
  //Tanstack: version 5 query implementation
  return useQuery({
    queryKey: [SherpaProjectReactQueryKey.GET_PROJECT_DOCUMENT, projectId, drawId, documentId, params],
    queryFn: () => getDocument({ projectId, drawId, documentId }),
    onSuccess,
    onError,
  });
};

interface InvoicesHookProps extends DrawsHookProps {
  drawId: number;
}

export const useInvoices = ({ projectId, drawId, params, onSuccess, onError }: InvoicesHookProps) => {
  //Tanstack: version 5 query implementation
  return useQuery({
    queryKey: [SherpaProjectReactQueryKey.GET_PROJECT_INVOICES, projectId, drawId, params],
    queryFn: () => getInvoices({ projectId, drawId, params }),
    onSuccess,
    onError,
    keepPreviousData: true,
  });
};

interface InvoiceHookProps extends DrawsHookProps {
  drawId: number;
  invoiceId: number;
}

export const useInvoice = ({ projectId, invoiceId, drawId, params, onSuccess, onError }: InvoiceHookProps) => {
  //Tanstack: version 5 query implementation
  return useQuery({
    queryKey: [SherpaProjectReactQueryKey.GET_PROJECT_INVOICE, projectId, drawId, invoiceId, params],
    queryFn: () => getInvoice({ projectId, drawId, invoiceId }),
    onSuccess,
    onError,
  });
};
